import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Task, useTasks } from '@/context/TaskContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import EisenhowerQuadrantColumn from './EisenhowerQuadrantColumn';
import { useQuadrantTasks } from './useQuadrantTasks';
import EisenhowerQuadrantGrid from './EisenhowerQuadrantGrid';
import UnprioritizedTaskList from './UnprioritizedTaskList';

const PriorityMatrixPanel: React.FC = () => {
  const { tasks, updateTask } = useTasks();
  const [showTaskList, setShowTaskList] = useState(false);
  const isMobile = useIsMobile();

  // Always get latest split for render!
  const quadrantResult = useQuadrantTasks(tasks);
  const { quadrantTasks, unprioritizedTasks, quadrants } = quadrantResult;

  // Helper to update task positions for a quadrant
  const reorderQuadrantPositions = (quadrantId: string, items: Task[]) => {
    items.forEach((task, idx) => {
      // Only update if priority_y changed, prevents excessive updates
      if (task.priority_y !== idx) {
        updateTask({ ...task, priority_y: idx });
      }
    });
  };

  // DnD Handler (only uses data available from render, NO hook calls)
  const onDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    // Always use quadrantTasks from current render
    // Defensive: fallback to empty arrays if undefined
    const currentQuadrantTasks = quadrantTasks;

    // Drag from task list to quadrant
    if (source.droppableId === 'unprioritized-list' && destination.droppableId.startsWith('quadrant-')) {
      const destQuadrant = destination.droppableId.replace('quadrant-', '');
      const draggedTask = tasks.find(t => t.id === draggableId);

      if (!draggedTask) return;

      // Insert task into correct quadrant+order
      updateTask({
        ...draggedTask,
        priority_quadrant: destQuadrant,
        priority_y: destination.index,
        priority_x: undefined, // not used here
      });

      // Prepare new order for quadrant after drop
      const destTasks = [...(currentQuadrantTasks[destQuadrant] ?? [])];
      destTasks.splice(destination.index, 0, { ...draggedTask, priority_quadrant: destQuadrant, priority_y: destination.index });
      reorderQuadrantPositions(destQuadrant, destTasks);

      toast.success(`Task moved to ${quadrants.find(q => q.id === destQuadrant)?.label}`);
      return;
    }

    // Drag within/between quadrants
    if (source.droppableId.startsWith('quadrant-') && destination.droppableId.startsWith('quadrant-')) {
      const sourceQuadrant = source.droppableId.replace('quadrant-', '');
      const destQuadrant = destination.droppableId.replace('quadrant-', '');

      if (sourceQuadrant !== destQuadrant) {
        // Move across quadrants
        const srcArray = [...(currentQuadrantTasks[sourceQuadrant] ?? [])];
        const destArray = [...(currentQuadrantTasks[destQuadrant] ?? [])];
        const [movingTask] = srcArray.splice(source.index, 1);
        if (!movingTask) return;

        destArray.splice(destination.index, 0, { ...movingTask, priority_quadrant: destQuadrant });

        // Update positions in both quadrants
        reorderQuadrantPositions(sourceQuadrant, srcArray);
        reorderQuadrantPositions(destQuadrant, destArray);

        toast.success(`Task moved to ${quadrants.find(q => q.id === destQuadrant)?.label}`);
      } else {
        // Reorder within the same quadrant
        const array = [...(currentQuadrantTasks[sourceQuadrant] ?? [])];
        const [moved] = array.splice(source.index, 1);
        if (!moved) return;
        array.splice(destination.index, 0, moved);
        reorderQuadrantPositions(sourceQuadrant, array);
      }
      return;
    }

    // Drag from quadrant to unprioritized list
    if (source.droppableId.startsWith('quadrant-') && destination.droppableId === 'unprioritized-list') {
      const sourceQuadrant = source.droppableId.replace('quadrant-', '');
      const srcArray = [...(currentQuadrantTasks[sourceQuadrant] ?? [])];
      srcArray.splice(source.index, 1);
      reorderQuadrantPositions(sourceQuadrant, srcArray);

      const draggedTask = tasks.find(t => t.id === draggableId);
      if (!draggedTask) return;

      updateTask({
        ...draggedTask,
        priority_quadrant: undefined,
        priority_y: undefined,
        priority_x: undefined
      });
      toast.success('Task priority removed');
      return;
    }
  };

  return (
    <div className="w-full max-w-[1200px] mx-auto mt-6">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className={cn(
          "flex gap-2 overflow-hidden",
          isMobile ? "flex-col" : "flex-row h-[calc(100vh-260px)]"
        )}>
          <UnprioritizedTaskList 
            tasks={unprioritizedTasks}
            showTaskList={showTaskList}
            setShowTaskList={setShowTaskList}
            isMobile={isMobile}
          />
          <div
            className={cn(
              isMobile ? "w-full flex-shrink-0 mt-4" : "flex-1 flex flex-col",
              "relative"
            )}
          >
            <EisenhowerQuadrantGrid 
              quadrants={quadrants}
              quadrantTasks={quadrantTasks}
            />
          </div>
        </div>
      </DragDropContext>
    </div>
  );
};

export default PriorityMatrixPanel;
